'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useState, useEffect } from 'react'

interface SearchAndFilterProps {
  categories: Array<{ category: string; count: number }>
  tags: Array<{ tag: string; count: number }>
  currentQuery?: string
  currentCategory?: string
  currentTag?: string
}

export default function SearchAndFilter({
  categories,
  tags,
  currentQuery = '',
  currentCategory = '',
  currentTag = '',
}: SearchAndFilterProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(currentQuery)
  const [showFilters, setShowFilters] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [tagFilter, setTagFilter] = useState('')

  useEffect(() => {
    setSearchQuery(currentQuery)
  }, [currentQuery])

  // Filter categories based on search
  const filteredCategories = categories.filter(({ category }) =>
    category.toLowerCase().includes(categoryFilter.toLowerCase())
  )

  // Filter tags based on search
  const filteredTags = tags.filter(({ tag }) =>
    tag.toLowerCase().includes(tagFilter.toLowerCase())
  )

  const buildUrl = (params: {
    query?: string
    category?: string
    tag?: string
    page?: number
  }) => {
    const urlParams = new URLSearchParams()
    
    if (params.query) urlParams.set('q', params.query)
    if (params.category) urlParams.set('category', params.category)
    if (params.tag) urlParams.set('tag', params.tag)
    if (params.page && params.page > 1) urlParams.set('page', params.page.toString())

    const queryString = urlParams.toString()
    return queryString ? `/blog?${queryString}` : '/blog'
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const url = buildUrl({
      query: searchQuery,
      category: currentCategory,
      tag: currentTag,
    })
    router.push(url)
  }

  const handleCategoryClick = (category: string) => {
    const url = buildUrl({
      query: currentQuery,
      category: category === currentCategory ? '' : category,
      tag: currentTag,
    })
    router.push(url)
  }

  const handleTagClick = (tag: string) => {
    const url = buildUrl({
      query: currentQuery,
      category: currentCategory,
      tag: tag === currentTag ? '' : tag,
    })
    router.push(url)
  }

  const clearFilters = () => {
    setSearchQuery('')
    router.push('/blog')
  }

  const hasActiveFilters = currentQuery || currentCategory || currentTag

  return (
    <div className="mb-8 space-y-4">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="relative">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles by title, content, or tags..."
            className="w-full px-4 py-3 pl-12 pr-24 text-slate-900 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-label="Search blog posts"
          />
          <svg
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            Search
          </button>
        </div>
      </form>

      {/* Filter Toggle Button (Mobile) */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="md:hidden w-full flex items-center justify-between px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
      >
        <span className="font-medium">
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </span>
        <svg
          className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Filters Section */}
      <div className={`space-y-4 ${showFilters ? 'block' : 'hidden md:block'}`}>
        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 p-4 bg-slate-50 rounded-lg">
            <span className="text-sm font-medium text-slate-700">Active filters:</span>
            {currentQuery && (
              <span className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Search: "{currentQuery}"
                <button
                  onClick={() => {
                    setSearchQuery('')
                    const url = buildUrl({ category: currentCategory, tag: currentTag })
                    router.push(url)
                  }}
                  className="ml-2 hover:text-primary/70"
                  aria-label="Clear search"
                >
                  ×
                </button>
              </span>
            )}
            {currentCategory && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                Category: {currentCategory}
                <button
                  onClick={() => handleCategoryClick(currentCategory)}
                  className="ml-2 hover:text-blue-600"
                  aria-label="Clear category filter"
                >
                  ×
                </button>
              </span>
            )}
            {currentTag && (
              <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                Tag: {currentTag}
                <button
                  onClick={() => handleTagClick(currentTag)}
                  className="ml-2 hover:text-green-600"
                  aria-label="Clear tag filter"
                >
                  ×
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="ml-auto text-sm text-slate-600 hover:text-slate-900 underline"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Categories */}
          {categories.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                Categories
              </h3>
              
              {/* Category Filter Input */}
              <div className="mb-3">
                <input
                  type="text"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  placeholder="Filter categories..."
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              {/* Scrollable Category List */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map(({ category, count }) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryClick(category)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                        currentCategory === category
                          ? 'bg-primary text-white'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span>{category}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          currentCategory === category
                            ? 'bg-white/20 text-white'
                            : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No categories found
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Popular Tags */}
          {tags.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                Popular Tags
              </h3>
              
              {/* Tag Filter Input */}
              <div className="mb-3">
                <input
                  type="text"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  placeholder="Filter tags..."
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              {/* Scrollable Tag List */}
              <div className="flex flex-wrap gap-2 max-h-[300px] overflow-y-auto pr-1">
                {filteredTags.length > 0 ? (
                  filteredTags.map(({ tag, count }) => (
                    <button
                      key={tag}
                      onClick={() => handleTagClick(tag)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm transition-colors ${
                        currentTag === tag
                          ? 'bg-primary text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      #{tag}
                      <span className="ml-1.5 text-xs opacity-75">({count})</span>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4 w-full">
                    No tags found
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
